import {
    adminMediaAssetControllerAbortUpload,
    adminMediaAssetControllerCompleteUpload,
    adminMediaAssetControllerInitiateUpload,
    adminMediaAssetControllerSignParts,
} from '../api/generated';
import type {
    InitiateUploadRequest,
    InitiateUploadResponse,
    MediaAssetDto,
    UploadPartUrl,
    UploadedPart,
} from '../api/generated';
import {isSuperAdminAuthority, isCompanyGroupAdminAuthority} from '../contexts/AuthContext';

/**
 * Control plane for resumable direct-to-S3 uploads.
 *
 * These four calls only ever broker signatures and state — the bytes never touch the API. The transfer
 * itself lives in MediaUploadService, which is the only intended caller.
 */

/**
 * Which of the three mounts of this surface the current persona may use.
 *
 * <p>The lifecycle is identical on all of them; what differs is who the asset belongs to. A company admin's
 * upload is owned by their company and reached by company. A super-admin curating the shared exercise
 * catalogue, and a group admin curating its own library, are both company-less, so their assets belong to
 * no company and are reached by ownership instead — a distinction the server has to make, because a
 * company-scoped guard cannot authorize a company-less asset at all.
 *
 * <p>Impersonation needs no case of its own: the mirrored authorities are the impersonated identity's, so
 * an impersonating super-admin takes the company path exactly as a direct company login does. Nor is
 * impersonation a substitute here — a super-admin edits a system exercise in place, while the same edit
 * made as an impersonated company admin forks a company-local copy.
 */
const basePath = (): string => {
    if (isSuperAdminAuthority()) return '/system/media/assets';
    if (isCompanyGroupAdminAuthority()) return '/groupAdmin/media/assets';
    return '/admin/media/assets';
};

/**
 * Preserve the axios-shaped error the upload dialogs already read (an `errorMessage` under the error's
 * `response.data`), matching what AuthService/ExcersiceService do for their migrated calls.
 */
const asAxiosError = (error: any, response?: Response): Error => {
    const err: any = new Error(error?.errorMessage || error?.message || 'Request failed');
    err.response = {data: error, status: response?.status};
    return err;
};

const bodyOrThrow = <T>(result: {data?: {body?: T}; error?: unknown; response?: Response}): T => {
    if (result.error) throw asAxiosError(result.error, result.response);
    const body = result.data?.body;
    if (body == null) {
        throw new Error('Media upload response was missing a body');
    }
    return body;
};

/*
 * The generated SDK functions spread their options over their own `url`, so passing one retargets the
 * call while keeping the generated types, client and error handling. The alternative — regenerating the
 * SDK — cannot come first: the snapshot is taken from a deployed server, and these endpoints are not
 * deployed yet. Refreshing it later will add the generated twins and these overrides can go.
 */

/** Open an upload and get back the presigned part plan. */
export const initiateUpload = async (request: InitiateUploadRequest): Promise<InitiateUploadResponse> =>
    bodyOrThrow(await adminMediaAssetControllerInitiateUpload({
        url: `${basePath()}/uploads`,
        body: request,
    } as any));

/** Fresh grants for parts whose presigned URLs expired mid-upload. */
export const signParts = async (assetId: number, partNumbers: number[]): Promise<UploadPartUrl[]> =>
    bodyOrThrow(await adminMediaAssetControllerSignParts({
        url: `${basePath()}/uploads/{id}/parts`,
        path: {id: assetId},
        body: {partNumbers},
    } as any));

/** Assemble the parts. Resolves once the asset is READY (or PROCESSING, if a transcoder is wired up). */
export const completeUpload = async (assetId: number, parts: UploadedPart[]): Promise<MediaAssetDto> =>
    bodyOrThrow(await adminMediaAssetControllerCompleteUpload({
        url: `${basePath()}/uploads/{id}/complete`,
        path: {id: assetId},
        body: {parts},
    } as any));

/**
 * Give up on an in-flight upload.
 *
 * Worth calling on every failure path: it releases the S3 parts (which are billed until collected) and
 * frees the caller's server-side in-flight upload slot, of which there are only a few. Returns void
 * rather than a body, so it doesn't go through bodyOrThrow.
 */
export const abortUpload = async (assetId: number): Promise<void> => {
    const {error, response} = await adminMediaAssetControllerAbortUpload({
        url: `${basePath()}/uploads/{id}`,
        path: {id: assetId},
    } as any);
    if (error) throw asAxiosError(error, response);
};

const mediaAssetService = {initiateUpload, signParts, completeUpload, abortUpload};

export default mediaAssetService;
